import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { BookOpen, CheckCircle2, Sparkles } from 'lucide-react'
import { learningApi, type LearningStep } from '@/api/learning'
import { articlesApi } from '@/api/articles'
import { ErrorScreen } from '@/components/ErrorScreen'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { PageHeader } from '@/components/PageHeader'
import { AchievementToast } from '@/components/achievements/AchievementToast'
import { KeepLearningScreen } from '@/components/learning/KeepLearningScreen'
import { MomentumOpeningCard } from '@/components/learning/MomentumOpeningCard'
import { SessionStepChallenge } from '@/components/learning/SessionStepChallenge'
import { StepNarration } from '@/components/learning/MomentumOpeningCard'
import { RecommendationWhyPanel } from '@/components/reading/RecommendationWhyPanel'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { copy } from '@/lib/copy'

function getArticleId(step: LearningStep): string | null {
  const id = step.config?.articleId
  return typeof id === 'string' ? id : null
}

function getQuestionCount(step: LearningStep): number {
  const count = step.config?.questionCount
  return typeof count === 'number' ? count : 5
}

interface ReadStepCardProps {
  step: LearningStep
  sessionId: string
  stepIndex: number
  onContinue: () => void
}

function ReadStepCard({ step, sessionId, stepIndex, onContinue }: ReadStepCardProps) {
  const { actions, learn } = useMinCopy()
  const navigate = useNavigate()
  const articleId = getArticleId(step)

  const articleQuery = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => articlesApi.getArticle(articleId!).then((r) => r.data),
    enabled: !!articleId,
  })

  const continueReadStepMutation = useMutation({
    mutationFn: () => learningApi.completeStep(sessionId, stepIndex),
    onSuccess: onContinue,
  })

  return (
    <Card className="p-6" data-testid="learning-step-read">
      <div className="mb-4 flex min-w-0 items-center gap-2 text-primary">
        <BookOpen className="h-5 w-5 shrink-0" />
        <h2 className="min-w-0 text-lg font-semibold">{step.title}</h2>
      </div>
      {step.narration ? <StepNarration message={step.narration} /> : null}
      {step.reasons && step.reasons.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {step.reasons.map((reason) => (
            <span
              key={reason}
              className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
            >
              {reason}
            </span>
          ))}
        </div>
      ) : null}
      <RecommendationWhyPanel explanations={step.explanations ?? []} className="mb-4" />
      <p className="mb-6 text-sm text-muted-foreground">
        {articleQuery.data?.isRead
          ? copy.actions.alreadyReadHint
          : learn.readThenFinish}
      </p>
      {articleQuery.data?.isRead ? (
        <Button
          className="min-h-12 w-full"
          loading={continueReadStepMutation.isPending}
          onClick={() => continueReadStepMutation.mutate()}
          data-testid="learning-continue-reading"
        >
          {copy.actions.continueSession}
        </Button>
      ) : (
        <Button
          className="min-h-12 w-full"
          disabled={!articleId}
          onClick={() => {
            if (!articleId) return
            navigate(
              `/reading/${articleId}?learningSessionId=${sessionId}&stepIndex=${stepIndex}`,
            )
          }}
          data-testid="learning-start-reading"
        >
          {actions.startReading}
        </Button>
      )}
    </Card>
  )
}

export function LearningSessionPage() {
  const { pages, learn } = useMinCopy()
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const sessionQuery = useQuery({
    queryKey: ['learningSession', sessionId],
    queryFn: () => learningApi.getSession(sessionId!),
    enabled: !!sessionId,
    refetchOnMount: 'always',
  })

  const handleStepComplete = () => {
    void queryClient.invalidateQueries({ queryKey: ['learningSession', sessionId] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  if (!sessionId) {
    return <ErrorScreen variant="not-found" title="Session not found" showHomeLink />
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (sessionQuery.isError || !sessionQuery.data) {
    return (
      <ErrorScreen
        variant="network"
        title="Could not load session"
        description="Please try again from the dashboard."
        onRetry={() => sessionQuery.refetch()}
      />
    )
  }

  const session = sessionQuery.data

  if (session.completed && session.summary) {
    const previewMessage =
      session.summary.momentumClosing?.tomorrowPreview.message ??
      (session.summary.tomorrowDueCount > 0
        ? `${session.summary.tomorrowDueCount} words ready to refresh tomorrow.`
        : 'We will pick up where you left off.')

    return (
      <div className="min-w-0 overflow-x-hidden">
        <KeepLearningScreen
          summary={{
            cardsReviewed: session.summary.wordsRefreshed,
            accuracy:
              session.summary.wordsRefreshed > 0
                ? Math.round(
                    (session.summary.correctCount / session.summary.wordsRefreshed) * 100,
                  )
                : session.summary.challengeScore > 0
                  ? session.summary.challengeScore
                  : 100,
            studyTimeMin: Math.max(1, Math.round(session.summary.durationSeconds / 60)),
            streakUpdated: session.summary.streakUpdated,
            currentStreak: session.summary.currentStreak,
          }}
          todayProgress={session.summary.todayProgress}
          tomorrowDueCount={session.summary.tomorrowDueCount}
          tomorrowPreviewMessage={previewMessage}
          growthHighlightMessage={session.summary.growthHighlight?.message}
          articlesRead={session.summary.articlesRead}
          challengeScore={session.summary.challengeScore}
        />
        <AchievementToast
          achievements={session.summary.newAchievements ?? []}
          storageKey={`achievement-toast-${session.id}`}
        />
      </div>
    )
  }

  if (session.steps.length === 0) {
    return (
      <div>
        <PageHeader title={pages.keepLearning} description={learn.nothingScheduled} />
        <KeepLearningScreen tomorrowDueCount={0} caughtUp />
      </div>
    )
  }

  const currentStep = session.currentStep
  const stepNumber = session.currentStepIndex + 1
  const totalSteps = session.steps.length
  const openingMessage = session.momentum?.opening?.message

  return (
    <div className="mx-auto min-w-0 max-w-2xl space-y-6 overflow-x-hidden" data-testid="learning-session-page">
      <PageHeader
        title={pages.continueLearning}
        description={formatMessage(learn.stepOf, { current: stepNumber, total: totalSteps })}
      />

      {openingMessage && session.currentStepIndex === 0 ? (
        <MomentumOpeningCard message={openingMessage} />
      ) : null}

      <div className="flex gap-2">
        {session.steps.map((step, index) => (
          <div
            key={`${step.type}-${index}`}
            className={`h-1.5 flex-1 rounded-full ${
              index < session.currentStepIndex
                ? 'bg-primary'
                : index === session.currentStepIndex
                  ? 'bg-primary/60'
                  : 'bg-muted'
            }`}
          />
        ))}
      </div>

      {!currentStep ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          Loading next step…
        </Card>
      ) : currentStep.type === 'REVIEW' ? (
        <Card className="p-6" data-testid="learning-step-review">
          <div className="mb-4 flex min-w-0 items-center gap-2 text-primary">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <h2 className="min-w-0 text-lg font-semibold">{currentStep.title}</h2>
          </div>
          {currentStep.narration ? <StepNarration message={currentStep.narration} /> : null}
          <p className="mb-6 text-sm text-muted-foreground">
            Refresh words that are due today — about {currentStep.estimatedMinutes} min.
          </p>
          <Button
            className="min-h-12 w-full"
            onClick={() =>
              navigate(
                `/review?learningSessionId=${session.id}&stepIndex=${session.currentStepIndex}`,
              )
            }
            data-testid="learning-start-review"
          >
            {learn.startRefresh}
          </Button>
        </Card>
      ) : currentStep.type === 'READ' ? (
        <ReadStepCard
          step={currentStep}
          sessionId={session.id}
          stepIndex={session.currentStepIndex}
          onContinue={handleStepComplete}
        />
      ) : currentStep.type === 'CHALLENGE' ? (
        <SessionStepChallenge
          sessionId={session.id}
          stepIndex={session.currentStepIndex}
          questionCount={getQuestionCount(currentStep)}
          narration={currentStep.narration}
          onComplete={handleStepComplete}
        />
      ) : currentStep.type === 'COACH' ? (
        <Card className="p-6" data-testid="learning-step-coach">
          <div className="mb-4 flex min-w-0 items-center gap-2 text-primary">
            <Sparkles className="h-5 w-5 shrink-0" />
            <h2 className="min-w-0 text-lg font-semibold">{currentStep.title}</h2>
          </div>
          {currentStep.narration ? <StepNarration message={currentStep.narration} /> : null}
          <p className="mb-6 text-sm text-muted-foreground">
            A focused exercise based on what you&apos;ve been learning.
          </p>
          <div className="flex flex-col gap-2">
            <Button
              className="min-h-12 w-full"
              onClick={() =>
                navigate(
                  `/ai-practice?learningSessionId=${session.id}&stepIndex=${session.currentStepIndex}`,
                )
              }
            >
              Open practice
            </Button>
            <Button
              className="min-h-12 w-full border border-border bg-background text-foreground shadow-none hover:bg-muted"
              onClick={async () => {
                await learningApi.completeStep(session.id, session.currentStepIndex)
                handleStepComplete()
              }}
              data-testid="learning-coach-skip"
            >
              Mark done
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-sm text-muted-foreground">
          Unknown step type: {currentStep.type}
        </Card>
      )}
    </div>
  )
}
